/**
 * Phase H (Δ-13) — responsive AppBar.
 *
 * CR-0014b polish (Δ-13b, 2026-05-30):
 *  - Mobile (<md): search icon MOVED to tab row. When expanded, tabs
 *    hide (not chips). Chips stay visible in their own row, centred.
 *  - Tablet (md to xl): 3-section flex. Tabs LEFT, search CENTRED,
 *    chips RIGHT. `pt-3` for breathing room from brand row.
 *  - Pill geometry on tabs/chips/search → rounded-md (handled in
 *    child components).
 *  - Desktop (≥xl): unchanged. Search centred between brand+tabs
 *    cluster (left) and chips+actions cluster (right).
 *
 * CR-0014c (Δ-13c, 2026-05-30) — pre-breakfast filter row (C-25):
 *  - `<ModeTabs />` self-gates and renders nothing pre-breakfast.
 *  - `<CategoryFilterChips />` already self-gates (Phase H).
 *  - `<SearchField />` remains mounted at every breakpoint pre-
 *    breakfast and switches to a wider variant inside the component.
 *  - MOBILE row 2: pre-breakfast we bypass the `mobileSearchExpanded`
 *    state machine and render <SearchField /> directly (which itself
 *    renders the always-expanded input via its `breakfastStarted`
 *    branch). Post-breakfast: the collapse-icon ↔ expanded-input
 *    state machine resumes unchanged.
 *  - TABLET row 2: visually collapses to a single centred search
 *    pill pre-breakfast (tabs + chips both hidden via self-gates).
 *    No structural change in this file — the `flex-1` centre wrapper
 *    naturally accommodates the wider input.
 *
 * CR-0030 (2026-06-03) — logout button + confirmation dialog.
 */
import { useState } from "react";
import { BarChart3, LogOut } from "lucide-react";
import { useNavigate } from "react-router-dom";
import useAppStore from "@/store/useAppStore";
import useAuthStore from "@/store/useAuthStore";
import { logoutApi } from "@/api/auth";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  StartBreakfastButton,
  ResetButton,
} from "@/components/controls/LifecycleButtons";
import ModeTabs from "@/components/appbar/ModeTabs";
import SearchField from "@/components/appbar/SearchField";
import CategoryFilterChips from "@/components/appbar/CategoryFilterChips";
import CategoryMultiSelectPanel from "@/components/category/CategoryMultiSelectPanel";

export default function AppBar() {
  const breakfastStarted = useAppStore((s) => s.appState?.breakfast_started);
  const [mobileSearchExpanded, setMobileSearchExpanded] = useState(false);
  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);
  const navigate = useNavigate();
  const token = useAuthStore((s) => s.token);
  const clearAuth = useAuthStore((s) => s.clearAuth);

  async function handleLogoutConfirm() {
    try {
      if (token) await logoutApi(token);
    } catch {
      /* swallow — clear locally regardless */
    }
    clearAuth();
    setLogoutDialogOpen(false);
    navigate("/login", { replace: true });
  }

  return (
    <header
      data-testid="app-bar"
      className="sticky top-0 z-50"
      style={{ backgroundColor: "#FFFFFF", borderBottom: "1px solid #E0E0E0" }}
    >
      {/* Row 1: brand left, lifecycle + report right. Desktop (≥xl)
          also inlines tabs (left) + centred search + right-aligned chips. */}
      <div className="flex items-center px-4 py-3 gap-3">
        <div className="flex items-center gap-3 shrink-0 min-w-0">
          <img
            data-testid="app-logo"
            src="/mygenie-logo.png"
            alt="MyGenie Hostess logo"
            className="rounded-lg shrink-0 object-contain"
            style={{ width: 36, height: 36 }}
          />
          <span
            className="text-base font-semibold truncate"
            style={{ color: "#424242" }}
          >
            MyGenie Hostess
          </span>
          <div className="hidden xl:flex ml-4">
            <ModeTabs />
          </div>
        </div>

        {/* Desktop centre — centred search at ≥xl */}
        <div className="hidden xl:flex flex-1 justify-center">
          <SearchField />
        </div>

        {/* Right cluster */}
        <div className="flex items-center gap-2 shrink-0 ml-auto xl:ml-0">
          <div className="hidden xl:flex">
            <CategoryFilterChips />
          </div>
          {breakfastStarted ? <ResetButton /> : <StartBreakfastButton />}
          <button
            data-testid="report-btn"
            onClick={() => navigate("/reports")}
            className="flex items-center justify-center rounded-lg p-2 hover:bg-gray-100 transition-colors"
            title="Reports"
          >
            <BarChart3 size={22} style={{ color: "#2196F3" }} />
          </button>
          <button
            data-testid="logout-btn"
            onClick={() => setLogoutDialogOpen(true)}
            className="flex items-center justify-center rounded-lg p-2 hover:bg-red-50 transition-colors"
            title="Logout"
          >
            <LogOut size={20} style={{ color: "#DC2626" }} />
          </button>
        </div>
      </div>

      {/* TABLET (md to <xl): 3-section row. Tabs left · search centred · chips right.
          `pt-3` gives breathing room from brand row. Δ-13b. */}
      <div
        data-testid="filter-row-tablet"
        className="hidden md:flex xl:hidden items-center gap-3 px-4 pb-3 pt-3 border-t"
        style={{ borderColor: "#F3F4F6", backgroundColor: "#FAFAFA" }}
      >
        <div className="shrink-0">
          <ModeTabs />
        </div>
        <div className="flex-1 flex justify-center">
          <SearchField />
        </div>
        <div className="shrink-0">
          <CategoryFilterChips />
        </div>
      </div>

      {/* MOBILE (<md) Row 2 — branches on `breakfastStarted` per Δ-13c.
          Pre-breakfast: <ModeTabs /> renders null (C-25), so the row
          contains only the always-expanded <SearchField /> filling
          the full width. Post-breakfast: original Δ-13b state machine
          (tabs + collapsed-icon, OR expanded search alone).         */}
      <div
        data-testid="filter-row-mobile-tabs"
        className="md:hidden flex items-center gap-2 px-4 pt-3 pb-1 border-t"
        style={{ borderColor: "#F3F4F6", backgroundColor: "#FAFAFA" }}
      >
        {!breakfastStarted && (
          <SearchField
            mobileExpanded={false}
            onToggleMobile={setMobileSearchExpanded}
          />
        )}
        {breakfastStarted && !mobileSearchExpanded && (
          <>
            <div className="flex-1 min-w-0 overflow-hidden">
              <ModeTabs />
            </div>
            <SearchField
              mobileExpanded={false}
              onToggleMobile={setMobileSearchExpanded}
            />
          </>
        )}
        {breakfastStarted && mobileSearchExpanded && (
          <SearchField
            mobileExpanded={true}
            onToggleMobile={setMobileSearchExpanded}
          />
        )}
      </div>

      {/* MOBILE (<md) Row 3 — chips CENTERED. Stays visible even when
          search is expanded. Δ-13b. */}
      {breakfastStarted && (
        <div
          data-testid="filter-row-mobile-chips"
          className="md:hidden flex items-center gap-2 px-4 pb-3 pt-2"
          style={{ backgroundColor: "#FAFAFA" }}
        >
          <CategoryFilterChips centerOnMobile />
        </div>
      )}

      {/* Sticky pre-breakfast category-assignment panel. Self-gates on
          selection.length === 0 (returns null). Lives inside the sticky
          header so it pins below the brand/filter rows when the grid
          scrolls. */}
      {!breakfastStarted && <CategoryMultiSelectPanel />}

      {/* Logout confirmation dialog */}
      <AlertDialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
        <AlertDialogContent
          data-testid="logout-confirm-dialog"
          className="w-[calc(100%-2rem)] max-w-[380px] max-h-[calc(100vh-2rem)] overflow-y-auto rounded-md"
        >
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure you want to logout?</AlertDialogTitle>
            <AlertDialogDescription>
              You will be redirected to the login screen.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="logout-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="logout-confirm"
              onClick={handleLogoutConfirm}
              style={{ backgroundColor: "#DC2626" }}
            >
              Logout
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </header>
  );
}
